/**
 * Commission Wallet Page
 * Module 6: Commission Wallet
 *
 * Dashboard for managing commission earnings.
 * Shows wallet balance, transactions, analytics, and actions.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { Skeleton } from 'primereact/skeleton';
import { Chart } from 'primereact/chart';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { Dialog } from 'primereact/dialog';
import { InputNumber } from 'primereact/inputnumber';
import { Calendar } from 'primereact/calendar';
import { TabView, TabPanel } from 'primereact/tabview';
import { Divider } from 'primereact/divider';
import { ProgressBar } from 'primereact/progressbar';
import { Tooltip } from 'primereact/tooltip';
import {
    walletService,
    TRANSACTION_TYPES,
    ANALYTICS_PERIODS,
    TRANSACTION_FILTER_OPTIONS,
    getTransactionTypeConfig,
    formatCurrency,
    formatTransactionAmount,
    formatDate,
    formatTimeAgo,
    getWalletHealth,
    prepareChartData,
    prepareBreakdownChart
} from '../../services/walletService';

/**
 * Format date for API using local timezone (YYYY-MM-DD)
 * Avoids UTC conversion issues with toISOString()
 */
const formatDateForApi = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const CommissionWallet = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const toast = useRef(null);

    // Wallet State
    const [loading, setLoading] = useState(true);
    const [wallet, setWallet] = useState(null);
    const [stats, setStats] = useState(null);

    // Transactions State
    const [transactions, setTransactions] = useState([]);
    const [transactionsLoading, setTransactionsLoading] = useState(false);
    const [transactionsPagination, setTransactionsPagination] = useState({
        total: 0,
        current_page: 1,
        per_page: 10,
        last_page: 1
    });
    const [transactionFilters, setTransactionFilters] = useState({
        type: null,
        date_from: null,
        date_to: null,
        page: 1
    });

    // Analytics State
    const [analytics, setAnalytics] = useState(null);
    const [analyticsLoading, setAnalyticsLoading] = useState(false);
    const [analyticsPeriod, setAnalyticsPeriod] = useState('month');

    // Dialog States
    const [showAddMoneyDialog, setShowAddMoneyDialog] = useState(false);
    const [addMoneyAmount, setAddMoneyAmount] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);
    const [addMoneySubmitted, setAddMoneySubmitted] = useState(false);

    // Active Tab
    const [activeTab, setActiveTab] = useState(0);

    // Fetch wallet details
    const fetchWallet = async () => {
        setLoading(true);
        try {
            const response = await walletService.getWallet();
            if (response.success) {
                setWallet(response.data.wallet);
                setStats(response.data.stats);
            } else {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: response.message || 'Failed to load wallet'
                });
            }
        } catch (error) {
            console.error('Failed to fetch wallet:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to load wallet data'
            });
        } finally {
            setLoading(false);
        }
    };

    // Fetch transactions
    const fetchTransactions = async () => {
        setTransactionsLoading(true);
        try {
            const filters = {
                ...transactionFilters,
                date_from: formatDateForApi(transactionFilters.date_from),
                date_to: formatDateForApi(transactionFilters.date_to),
                per_page: transactionsPagination.per_page
            };

            const response = await walletService.getTransactions(filters);
            if (response.success) {
                setTransactions(response.data.transactions || []);
                setTransactionsPagination(response.data.pagination || {});
            }
        } catch (error) {
            console.error('Failed to fetch transactions:', error);
        } finally {
            setTransactionsLoading(false);
        }
    };

    // Fetch analytics
    const fetchAnalytics = async () => {
        setAnalyticsLoading(true);
        try {
            const response = await walletService.getAnalytics(analyticsPeriod);
            if (response.success) {
                setAnalytics(response.data);
            }
        } catch (error) {
            console.error('Failed to fetch analytics:', error);
        } finally {
            setAnalyticsLoading(false);
        }
    };

    // Initial load
    useEffect(() => {
        fetchWallet();
    }, []);

    // Handle payment return from gateway
    useEffect(() => {
        const paymentStatus = searchParams.get('payment_status');
        const paymentMessage = searchParams.get('payment_message');

        if (paymentStatus) {
            // Show toast based on payment status
            if (paymentStatus === 'success') {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Payment Successful',
                    detail: paymentMessage || 'Money added to wallet successfully!',
                    life: 5000
                });
                // Refresh wallet data
                fetchWallet();
                fetchTransactions();
            } else if (paymentStatus === 'cancelled') {
                toast.current?.show({
                    severity: 'warn',
                    summary: 'Payment Cancelled',
                    detail: paymentMessage || 'Payment was cancelled',
                    life: 5000
                });
            } else if (paymentStatus === 'failed') {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Payment Failed',
                    detail: paymentMessage || 'Payment could not be completed',
                    life: 5000
                });
            }

            // Clear the query params from URL
            setSearchParams({});
        }
    }, [searchParams]);

    // Fetch transactions when filters change
    useEffect(() => {
        fetchTransactions();
    }, [transactionFilters]);

    // Fetch analytics when period changes or tab changes
    useEffect(() => {
        if (activeTab === 1) {
            fetchAnalytics();
        }
    }, [analyticsPeriod, activeTab]);

    // Handle add money
    const handleAddMoney = async () => {
        setAddMoneySubmitted(true);
        if (!addMoneyAmount || addMoneyAmount < 100) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Invalid Amount',
                detail: 'Minimum amount is ₹100'
            });
            return;
        }

        setActionLoading(true);
        try {
            // Step 1: Create Razorpay order
            const orderResponse = await walletService.createAddMoneyOrder(addMoneyAmount);
            if (!orderResponse.success) {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: orderResponse.message || 'Failed to create payment order'
                });
                return;
            }

            // Step 2: Generate payment token for cross-tenant redirect
            const tokenResponse = await walletService.generatePaymentToken({
                order_id: orderResponse.data.order_id,
                payment_confirm_token: orderResponse.data.payment_confirm_token
            });

            if (!tokenResponse.success) {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: tokenResponse.message || 'Failed to generate payment token'
                });
                return;
            }

            // Step 3: Show info and redirect to payment gateway
            toast.current?.show({
                severity: 'info',
                summary: 'Redirecting...',
                detail: 'Opening secure payment gateway'
            });

            setShowAddMoneyDialog(false);
            setAddMoneyAmount(null);
            setAddMoneySubmitted(false);

            // Redirect to payment gateway
            window.location.href = tokenResponse.data.payment_url;

        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.message || 'Failed to initiate payment'
            });
        } finally {
            setActionLoading(false);
        }
    };

    // Handle export
    const handleExport = async (format) => {
        try {
            await walletService.downloadTransactions({
                format,
                date_from: formatDateForApi(transactionFilters.date_from),
                date_to: formatDateForApi(transactionFilters.date_to),
                type: transactionFilters.type
            });
            toast.current?.show({
                severity: 'success',
                summary: 'Export Complete',
                detail: `Transactions exported as ${format.toUpperCase()}`
            });
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Export Failed',
                detail: 'Failed to export transactions'
            });
        }
    };

    // Chart data
    const getEarningsChartData = () => {
        if (!analytics?.charts?.earnings_by_date) return null;
        return prepareChartData(analytics.charts.earnings_by_date, 'Earnings', '#22C55E');
    };

    const getSpendingChartData = () => {
        if (!analytics?.charts?.spending_by_date) return null;
        return prepareChartData(analytics.charts.spending_by_date, 'Spending', '#EF4444');
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: 'bottom' }
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    callback: (value) => '₹' + value.toLocaleString('en-IN')
                }
            }
        }
    };

    // Loading skeleton
    if (loading) {
        return (
            <div className="commission-wallet">
                <div className="grid">
                    <div className="col-12 lg:col-4">
                        <Card><Skeleton height="180px" /></Card>
                    </div>
                    <div className="col-12 lg:col-8">
                        <div className="grid">
                            {[1, 2, 3, 4].map(i => (
                                <div key={i} className="col-6">
                                    <Card><Skeleton height="80px" /></Card>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <Card className="mt-4"><Skeleton height="400px" /></Card>
            </div>
        );
    }

    const walletHealth = getWalletHealth(wallet);

    return (
        <div className="commission-wallet">
            <Toast ref={toast} />

            {/* Header */}
            <div className="surface-card border-round shadow-1 p-4 mb-4">
                <div className="flex flex-column lg:flex-row lg:align-items-center lg:justify-content-between gap-3">
                    <div className="flex align-items-center gap-2">
                        <Button
                            icon="pi pi-arrow-left"
                            aria-label="Back to Marketplace"
                            className="p-button-text p-button-rounded"
                            onClick={() => navigate('/app/marketplace')}
                            tooltip="Back to Marketplace"
                        />
                        <div>
                            <h2 className="text-900 font-bold m-0 mb-1">Commission Wallet</h2>
                            <p className="text-600 m-0">Manage your commission earnings</p>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <Button
                            label="Add Money"
                            icon="pi pi-plus"
                            className="p-button-success"
                            onClick={() => setShowAddMoneyDialog(true)}
                        />
                    </div>
                </div>
            </div>

            {/* Wallet Balance Card & Stats */}
            <div className="grid mb-4">
                {/* Main Balance Card */}
                <div className="col-12 lg:col-4">
                    <Card className="shadow-2 h-full" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                        <div className="text-white">
                            <div className="flex align-items-center justify-content-between mb-3">
                                <span className="text-white-alpha-80">Available Balance</span>
                                <Tag
                                    value={walletHealth.label}
                                    severity={walletHealth.status === 'healthy' ? 'success' : walletHealth.status === 'moderate' ? 'warning' : 'danger'}
                                    className="text-xs"
                                />
                            </div>
                            <div className="text-4xl font-bold mb-4">
                                {formatCurrency(wallet?.available_balance || 0)}
                            </div>

                            <Divider className="my-3" style={{ borderColor: 'rgba(255,255,255,0.2)' }} />

                            <div className="grid">
                                <div className="col-6">
                                    <div className="text-white-alpha-70 text-xs mb-1 flex align-items-center gap-1">
                                        <span>Locked</span>
                                        <i
                                            className="pi pi-info-circle text-white-alpha-70 cursor-pointer locked-balance-info"
                                            data-pr-tooltip="Funds reserved for pending withdrawals or deals. Will be released when transactions complete."
                                            data-pr-position="top"
                                        />
                                        <Tooltip target=".locked-balance-info" />
                                    </div>
                                    <div className="text-xl font-semibold">
                                        {formatCurrency(wallet?.locked_balance || 0)}
                                    </div>
                                </div>
                                <div className="col-6">
                                    <div className="text-white-alpha-70 text-xs mb-1">Total</div>
                                    <div className="text-xl font-semibold">
                                        {formatCurrency(wallet?.total_balance || 0)}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Card>
                </div>

                {/* Stats Cards */}
                <div className="col-12 lg:col-8">
                    <div className="grid h-full">
                        {[
                            {
                                label: 'Lifetime Earned',
                                value: formatCurrency(wallet?.lifetime_earned || 0, false),
                                icon: 'pi-trending-up',
                                color: 'green',
                                bgColor: '#f0fdf4'
                            },
                            {
                                label: 'This Month',
                                value: formatCurrency(wallet?.current_month_earned || 0, false),
                                icon: 'pi-calendar',
                                color: 'blue',
                                bgColor: '#eff6ff'
                            },
                            {
                                label: 'Total Withdrawn',
                                value: formatCurrency(wallet?.lifetime_withdrawn || 0, false),
                                icon: 'pi-arrow-up-right',
                                color: 'purple',
                                bgColor: '#faf5ff'
                            },
                            {
                                label: 'Recent Transactions',
                                value: stats?.recent_transactions_count || 0,
                                icon: 'pi-list',
                                color: 'orange',
                                bgColor: '#fff7ed',
                                suffix: 'in 30 days'
                            }
                        ].map((stat, idx) => (
                            <div key={idx} className="col-6">
                                <Card className="shadow-1 h-full">
                                    <div className="flex align-items-center gap-3">
                                        <div
                                            className="flex align-items-center justify-content-center border-round-lg"
                                            style={{
                                                width: '48px',
                                                height: '48px',
                                                backgroundColor: stat.bgColor
                                            }}
                                        >
                                            <i className={`pi ${stat.icon} text-${stat.color}-500 text-xl`} />
                                        </div>
                                        <div>
                                            <div className="text-500 text-sm mb-1">{stat.label}</div>
                                            <div className="text-900 font-bold text-xl">{stat.value}</div>
                                            {stat.suffix && (
                                                <div className="text-400 text-xs">{stat.suffix}</div>
                                            )}
                                        </div>
                                    </div>
                                </Card>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Tabs: Transactions & Analytics */}
            <Card className="shadow-1">
                <TabView activeIndex={activeTab} onTabChange={(e) => setActiveTab(e.index)}>
                    {/* Transactions Tab */}
                    <TabPanel header="Transactions" leftIcon="pi pi-list mr-2">
                        {/* Filters */}
                        <div className="flex flex-wrap gap-2 mb-4">
                            <Dropdown
                                value={transactionFilters.type}
                                options={TRANSACTION_FILTER_OPTIONS}
                                onChange={(e) => setTransactionFilters(prev => ({
                                    ...prev,
                                    type: e.value,
                                    page: 1
                                }))}
                                placeholder="All Types"
                                className="w-12rem"
                            />
                            <Calendar
                                value={transactionFilters.date_from}
                                onChange={(e) => setTransactionFilters(prev => ({
                                    ...prev,
                                    date_from: e.value,
                                    page: 1
                                }))}
                                placeholder="From Date"
                                showIcon
                                className="w-10rem"
                            />
                            <Calendar
                                value={transactionFilters.date_to}
                                onChange={(e) => setTransactionFilters(prev => ({
                                    ...prev,
                                    date_to: e.value,
                                    page: 1
                                }))}
                                placeholder="To Date"
                                showIcon
                                className="w-10rem"
                            />
                            <Button
                                icon="pi pi-filter-slash"
                                className="p-button-outlined"
                                onClick={() => setTransactionFilters({
                                    type: null,
                                    date_from: null,
                                    date_to: null,
                                    page: 1
                                })}
                                tooltip="Clear Filters"
                            />
                            <div className="flex-grow-1" />
                            <Button
                                icon="pi pi-download"
                                label="Export"
                                className="p-button-outlined"
                                onClick={() => handleExport('csv')}
                            />
                        </div>

                        {/* Transactions Table */}
                        <DataTable
                            value={transactions}
                            loading={transactionsLoading}
                            responsiveLayout="scroll"
                            emptyMessage="No transactions found"
                            className="p-datatable-sm"
                            paginator
                            rows={transactionsPagination.per_page}
                            totalRecords={transactionsPagination.total}
                            lazy
                            first={(transactionFilters.page - 1) * transactionsPagination.per_page}
                            onPage={(e) => setTransactionFilters(prev => ({
                                ...prev,
                                page: Math.floor(e.first / e.rows) + 1
                            }))}
                        >
                            <Column
                                header="Type"
                                body={(row) => {
                                    const config = getTransactionTypeConfig(row.type);
                                    return (
                                        <div className="flex align-items-center gap-2">
                                            <div
                                                className="flex align-items-center justify-content-center border-round"
                                                style={{
                                                    width: '32px',
                                                    height: '32px',
                                                    backgroundColor: config.bgColor
                                                }}
                                            >
                                                <i
                                                    className={`pi ${config.icon}`}
                                                    style={{ color: config.color }}
                                                />
                                            </div>
                                            <div>
                                                <div className="font-medium text-900 text-sm">
                                                    {config.label}
                                                </div>
                                                <div className="text-500 text-xs">
                                                    {formatTimeAgo(row.created_at)}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                }}
                                style={{ minWidth: '180px' }}
                            />
                            <Column
                                header="Description"
                                field="description"
                                body={(row) => (
                                    <span className="text-600 text-sm">
                                        {row.description || '-'}
                                    </span>
                                )}
                                style={{ minWidth: '200px' }}
                            />
                            <Column
                                header="Amount"
                                body={(row) => {
                                    const { display, color } = formatTransactionAmount(row.amount, row.is_incoming);
                                    return (
                                        <span className="font-semibold" style={{ color }}>
                                            {display}
                                        </span>
                                    );
                                }}
                                style={{ minWidth: '120px' }}
                            />
                            <Column
                                header="Balance After"
                                body={(row) => (
                                    <span className="text-600">
                                        {formatCurrency(row.balance_after)}
                                    </span>
                                )}
                                style={{ minWidth: '120px' }}
                            />
                            <Column
                                header="Date"
                                body={(row) => (
                                    <span className="text-500 text-sm">
                                        {formatDate(row.created_at)}
                                    </span>
                                )}
                                style={{ minWidth: '150px' }}
                            />
                        </DataTable>
                    </TabPanel>

                    {/* Analytics Tab */}
                    <TabPanel header="Analytics" leftIcon="pi pi-chart-line mr-2">
                        {/* Period Selector */}
                        <div className="flex justify-content-between align-items-center mb-4">
                            <h4 className="text-900 font-semibold m-0">Earnings Analytics</h4>
                            <Dropdown
                                value={analyticsPeriod}
                                options={ANALYTICS_PERIODS}
                                onChange={(e) => setAnalyticsPeriod(e.value)}
                                className="w-10rem"
                            />
                        </div>

                        {analyticsLoading ? (
                            <div className="grid">
                                <div className="col-12 lg:col-6"><Skeleton height="300px" /></div>
                                <div className="col-12 lg:col-6"><Skeleton height="300px" /></div>
                            </div>
                        ) : analytics ? (
                            <>
                                {/* Summary Cards */}
                                <div className="grid mb-4">
                                    {[
                                        {
                                            label: 'Total Earnings',
                                            value: analytics.summary?.formatted?.total_earnings,
                                            color: 'green'
                                        },
                                        {
                                            label: 'Total Spending',
                                            value: analytics.summary?.formatted?.total_spending,
                                            color: 'red'
                                        },
                                        {
                                            label: 'Net Change',
                                            value: analytics.summary?.formatted?.net_change,
                                            color: analytics.summary?.net_change >= 0 ? 'green' : 'red'
                                        }
                                    ].map((item, idx) => (
                                        <div key={idx} className="col-12 md:col-4">
                                            <div className={`surface-100 border-round p-3 text-center`}>
                                                <div className="text-500 text-sm mb-1">{item.label}</div>
                                                <div className={`text-${item.color}-500 font-bold text-2xl`}>
                                                    {item.value || '₹0.00'}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Charts */}
                                <div className="grid">
                                    <div className="col-12 lg:col-6">
                                        <div className="surface-50 border-round p-3">
                                            <h5 className="text-900 font-semibold m-0 mb-3">Earnings Trend</h5>
                                            <div style={{ height: '280px' }}>
                                                {getEarningsChartData() ? (
                                                    <Chart
                                                        type="line"
                                                        data={getEarningsChartData()}
                                                        options={chartOptions}
                                                    />
                                                ) : (
                                                    <div className="flex align-items-center justify-content-center h-full text-500">
                                                        <div className="text-center">
                                                            <i className="pi pi-chart-line text-4xl mb-2" />
                                                            <p>No earnings data</p>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-12 lg:col-6">
                                        <div className="surface-50 border-round p-3">
                                            <h5 className="text-900 font-semibold m-0 mb-3">Spending Trend</h5>
                                            <div style={{ height: '280px' }}>
                                                {getSpendingChartData() ? (
                                                    <Chart
                                                        type="line"
                                                        data={getSpendingChartData()}
                                                        options={chartOptions}
                                                    />
                                                ) : (
                                                    <div className="flex align-items-center justify-content-center h-full text-500">
                                                        <div className="text-center">
                                                            <i className="pi pi-chart-line text-4xl mb-2" />
                                                            <p>No spending data</p>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Transaction Breakdown */}
                                {analytics.breakdown?.transaction_counts && (
                                    <div className="mt-4">
                                        <div className="flex align-items-center gap-2 mb-3">
                                            <h5 className="text-900 font-semibold m-0">Transaction Breakdown</h5>
                                            <i
                                                className="pi pi-info-circle text-500 cursor-pointer transaction-breakdown-info"
                                                data-pr-tooltip="Shows the count of each transaction type during the selected period. Helps you understand your wallet activity patterns."
                                                data-pr-position="top"
                                            />
                                            <Tooltip target=".transaction-breakdown-info" />
                                        </div>
                                        <div className="grid">
                                            {Object.entries(analytics.breakdown.transaction_counts).map(([type, count]) => {
                                                const config = getTransactionTypeConfig(type);
                                                return (
                                                    <div key={type} className="col-6 md:col-4 lg:col-2">
                                                        <div className="surface-50 border-round p-3 text-center">
                                                            <i
                                                                className={`pi ${config.icon} text-2xl mb-2`}
                                                                style={{ color: config.color }}
                                                            />
                                                            <div className="font-bold text-900">{count}</div>
                                                            <div className="text-500 text-xs">{config.label}</div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="flex flex-column align-items-center py-6">
                                <i className="pi pi-chart-bar text-4xl text-400 mb-3" />
                                <p className="text-500">No analytics data available</p>
                            </div>
                        )}
                    </TabPanel>
                </TabView>
            </Card>

            {/* Add Money Dialog */}
            <Dialog
                visible={showAddMoneyDialog}
                onHide={() => {
                    setShowAddMoneyDialog(false);
                    setAddMoneyAmount(null);
                    setAddMoneySubmitted(false);
                }}
                header="Add Money to Wallet"
                style={{ width: '400px' }}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button
                            label="Cancel"
                            className="p-button-text"
                            onClick={() => setShowAddMoneyDialog(false)}
                        />
                        <Button
                            label="Proceed to Pay"
                            icon="pi pi-credit-card"
                            className="p-button-success"
                            onClick={handleAddMoney}
                            loading={actionLoading}
                            disabled={!addMoneyAmount || addMoneyAmount < 100}
                        />
                    </div>
                }
            >
                <div className="p-fluid">
                    <div className="field">
                        <label htmlFor="amount">Amount (₹)</label>
                        <InputNumber
                            id="amount"
                            value={addMoneyAmount}
                            onValueChange={(e) => setAddMoneyAmount(e.value)}
                            mode="currency"
                            currency="INR"
                            locale="en-IN"
                            min={100}
                            max={100000}
                            placeholder="Enter amount"
                            className={addMoneySubmitted && (!addMoneyAmount || addMoneyAmount < 100) ? 'p-invalid' : ''}
                        />
                        {addMoneySubmitted && !addMoneyAmount && (
                            <small className="p-error">Amount is required.</small>
                        )}
                        {addMoneySubmitted && addMoneyAmount && addMoneyAmount < 100 && (
                            <small className="p-error">Minimum amount is ₹100.</small>
                        )}
                        {!(addMoneySubmitted && (!addMoneyAmount || addMoneyAmount < 100)) && (
                            <small className="text-500">Min: ₹100 | Max: ₹1,00,000</small>
                        )}
                    </div>

                    <div className="flex flex-wrap gap-2 mt-3">
                        {[500, 1000, 2000, 5000].map((amount) => (
                            <Button
                                key={amount}
                                label={`₹${amount.toLocaleString('en-IN')}`}
                                className="p-button-outlined p-button-sm"
                                onClick={() => setAddMoneyAmount(amount)}
                            />
                        ))}
                    </div>
                </div>
            </Dialog>

        </div>
    );
};

export default CommissionWallet;
